/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { useHistory } from 'react-router';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { StackBladeHost } from 'src/core/components/StackBladeHost';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { useModelGuidanceCardsData } from 'src/customText/core/hooks/useModelGuidanceCardsData';
import { TestBladeHeader } from 'src/luis/projects/testPane/components/common/TestBladeHeader';
import { ModelGuidanceCardMessages as messages } from 'src/modules/modelGuidance/components/modelGuidanceCardMessages';
import {
    getRouteToDataTaggingPage,
    getSingleModelName,
    handleGetLabel,
    SchemaType
} from 'src/modules/modelGuidance/modelGuidanceUtils/modelGuidanceUtils';
import { ModelGuidanceStore } from 'src/modules/modelGuidance/stores/ModelGuidanceStore';
import styled from 'styled-components';

type GuidanceDetailsBladeProps = {
    isOpen: boolean;
    projectName: string;
    guidanceKey: string;
    onDismiss: () => void;
    pivotName?: SchemaType;
    isExtractionProject: boolean;
    modelGuidanceStore: ModelGuidanceStore;
    onLinkToOtherTabClick: (tabKey: string) => void;
    shouldNavigateToDataTagging: (filters: string[]) => boolean;
    isRecommendationLinkDisabled: (items: string[], pivotName: SchemaType) => boolean;
};

type GuidanceErrorBladeContent = {
    cardKey: string;
    labels?: string[];
    errorDescription: string;
    descriptionLinkText: string;
    recommendationTitle: string;
    recommendationDescription: string;
    tabKeyToRedirectOnLinkClick: string;
};

const headerTheme = getScopedTheme('GlobalHeaderBlades');

const Title = styled(LuisText)({
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px'
});

export const GuidanceDetailsBlade = (props: GuidanceDetailsBladeProps) => {
    const {
        isOpen,
        onDismiss,
        projectName,
        pivotName,
        guidanceKey,
        isExtractionProject,
        modelGuidanceStore,
        onLinkToOtherTabClick,
        shouldNavigateToDataTagging,
        isRecommendationLinkDisabled
    } = props;

    const history = useHistory();
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const [lastFocusedElement, setLastFocusedElement] = React.useState(null);

    React.useEffect(() => {
        document.getElementById(lastFocusedElement)?.focus();
    }, [lastFocusedElement]);

    const getAppliedFilters = (cardKey: string, index: number) => {
        switch (cardKey) {
            case 'not-enough-data-card':
                return [modelGuidanceStore.insufficientExamples[index]];
            case 'missing-labels-card':
                return [modelGuidanceStore.missingLabels[index]];
            case 'confusion-labels-card':
                return [
                    Object.keys(modelGuidanceStore.confusionLabels[index])[0],
                    Object.values(modelGuidanceStore.confusionLabels[index])[0]
                ];
            default:
                return [];
        }
    };

    const handleRecommendationClick = (linkId: string, cardKey: string, index: number) => {
        setLastFocusedElement(linkId);
        const dataToFilter = getAppliedFilters(cardKey, index);
        if (shouldNavigateToDataTagging(dataToFilter)) {
            history.push(getRouteToDataTaggingPage(projectName, modelGuidanceStore?.projectKind), {
                cardKey,
                tab: pivotName !== null ? 'distribution' : 'recommendations',
                model: modelGuidanceStore?.modelName,
                ...(pivotName === 'Entities'
                    ? { entitiesIdsFilters: getAppliedFilters(cardKey, index) }
                    : { intentsIdsFilters: getAppliedFilters(cardKey, index) })
            });
        } else {
            history.push(routeTable.orchestration.buildSchemaPage(projectName));
        }
    };

    const improveModelBladeContent = (
        <Stack styles={{ root: { marginTop: '20px' } }} tokens={{ childrenGap: '20px' }}>
            <LuisText>{localize(messages.improveModelSubtitle)}</LuisText>

            <Stack styles={{ root: { margin: '20px 25px' } }} tokens={{ childrenGap: '20px' }}>
                <LuisText>
                    {'1. '}
                    <ExternalLink role={'link'} onClick={() => onLinkToOtherTabClick('test-set-tab')}>
                        {localize(messages.improveModelFirstActionLinkText)}
                    </ExternalLink>
                    {localize(messages.improveModelFirstActionDescription)}
                </LuisText>

                {serviceName !== 'customSentiment' && (
                    <LuisText>
                        {'2. '}
                        <ExternalLink role={'link'} onClick={() => onLinkToOtherTabClick('data-distribution-tab')}>
                            {localize(messages.improveModelSecondActionLinkText)}
                        </ExternalLink>
                        {localize(messages.improveModelSecondActionDescription, {
                            labelType: localize(getSingleModelName(pivotName, serviceName)),
                            pluralLabelType: localize(handleGetLabel(pivotName, serviceName, false))
                        })}
                    </LuisText>
                )}

                <LuisText>
                    {serviceName === 'customSentiment' ? '2. ' : '3. '}
                    <ExternalLink
                        role="link"
                        onClick={() => history.push(getRouteToDataTaggingPage(projectName, modelGuidanceStore?.projectKind))}>
                        {localize(messages.improveModelThirdActionLinkText, {
                            type: localize(pivotName ? messages.utterances : messages.documents)
                        })}
                    </ExternalLink>
                    {localize(messages.improveModelThirdActionDescription)}
                </LuisText>
            </Stack>
        </Stack>
    );

    const GuidanceErrorBladeContent = (bladeProps: GuidanceErrorBladeContent) => {
        const {
            labels,
            cardKey,
            errorDescription,
            descriptionLinkText,
            recommendationTitle,
            recommendationDescription,
            tabKeyToRedirectOnLinkClick
        } = bladeProps;

        return (
            <Stack styles={{ root: { marginTop: '20px' } }} tokens={{ childrenGap: '20px' }}>
                <LuisText>{errorDescription}</LuisText>

                {serviceName !== 'customSentiment' && (
                    <LuisText>
                        {localize(messages.check)}
                        <ExternalLink role={'link'} onClick={() => onLinkToOtherTabClick(tabKeyToRedirectOnLinkClick)}>
                            {descriptionLinkText}
                        </ExternalLink>
                        {localize(messages.forDetails)}
                    </LuisText>
                )}

                {labels?.length > 0 && (
                    <Stack styles={{ root: { marginTop: '20px' } }} tokens={{ childrenGap: '20px' }}>
                        <Stack>
                            <Title>{localize(messages.recommendation)}</Title>

                            <LuisText>{recommendationDescription}</LuisText>
                        </Stack>

                        <Stack>
                            <Title>{recommendationTitle}</Title>

                            {labels.map((label, index) => (
                                <ExternalLink
                                    key={label}
                                    role={'link'}
                                    id={`${cardKey}-${index}`}
                                    onClick={() => handleRecommendationClick(`${cardKey}-${index}`, cardKey, index)}
                                    disabled={isRecommendationLinkDisabled(getAppliedFilters(cardKey, index), pivotName)}>
                                    {label}
                                </ExternalLink>
                            ))}
                        </Stack>
                    </Stack>
                )}
            </Stack>
        );
    };

    const { modelGuidanceCardBladeHeader, modelGuidanceCardsInfo } = useModelGuidanceCardsData({
        pivotName,
        modelGuidanceStore,
        isExtractionProject
    });

    const bladeContent = React.useMemo(
        (): JSX.Element =>
            guidanceKey === 'improve-model' ? (
                improveModelBladeContent
            ) : (
                <GuidanceErrorBladeContent {...modelGuidanceCardsInfo[guidanceKey]} />
            ),
        [guidanceKey, modelGuidanceCardsInfo]
    );

    const blades = React.useMemo(
        () => [
            {
                key: guidanceKey,
                header: <TestBladeHeader>{modelGuidanceCardBladeHeader[guidanceKey]}</TestBladeHeader>,
                content: bladeContent
            }
        ],
        [guidanceKey]
    );

    return (
        <StackBladeHost
            isOpen={isOpen}
            blades={blades}
            onDismiss={onDismiss}
            onLastBladeDismissed={onDismiss}
            topSpacing={headerTheme.topSpacing}
        />
    );
};
